
import { useId } from 'react';
import { Button, Input, Row, Spacer, Stack, Text } from '@client/shared/design';
import { cx } from '@client/shared/lib/cx.ts';
import { MAX_UNAVAILABILITY_REASON_LENGTH } from '@shared/index';
import { formatListingClock, formatListingRange } from './listing-zone-time.ts';
import type { EditBounds, EditTimes } from '@client/shared/calendar/editable-span.ts';
import styles from './ListingDetails.module.css';

export interface BlockConfirmEdit {
  readonly times: EditTimes;
  readonly bounds: EditBounds;
  readonly onAdjust: (edge: 'start' | 'end', delta: -1 | 1) => void;
}

export interface BlockConfirmBodyProps {
  ranges: readonly { start: string; end: string }[];
  zone: string | null;
  edit?: BlockConfirmEdit | null;
  draft: string;
  onDraftChange: (value: string) => void;
  busy: boolean;
  conflict: boolean;
  onCommit: () => void;
  onCancel: () => void;
}

export function BlockConfirmBody({
  ranges,
  zone,
  edit,
  draft,
  onDraftChange,
  busy,
  conflict,
  onCommit,
  onCancel,
}: BlockConfirmBodyProps) {
  const count = ranges.length;
  const reasonId = useId();
  return (
    <Stack gap={3}>
      {edit ? (
        <TimeRangeEditor
          editTimes={edit.times}
          editBounds={edit.bounds}
          zone={zone}
          onAdjust={edit.onAdjust}
          disabled={busy}
        />
      ) : count === 1 ? (
        <Text size="sm" weight="medium">
          <time className={cx(styles.bookingRange, 'tnum')}>
            {formatListingRange(new Date(ranges[0]!.start), new Date(ranges[0]!.end), zone)}
          </time>
        </Text>
      ) : (
        <Stack gap={1}>
          <Text size="sm" weight="medium">
            <span className="tnum">{count}</span> time ranges
          </Text>
          {ranges.map((r) => (
            <Text key={`${r.start}-${r.end}`} as="span" size="xs" tone="soft">
              <time className={cx(styles.bookingRange, 'tnum')}>
                {formatListingRange(new Date(r.start), new Date(r.end), zone)}
              </time>
            </Text>
          ))}
        </Stack>
      )}

      <Stack gap={1}>
        <label htmlFor={reasonId} className={styles.reasonLabel}>
          Reason <span className={styles.reasonLabelOptional}>(optional)</span>
        </label>
        <Input
          id={reasonId}
          autoFocus
          value={draft}
          maxLength={MAX_UNAVAILABILITY_REASON_LENGTH}
          placeholder="e.g. Staging photos"
          disabled={busy}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onCommit();
            }
          }}
        />
      </Stack>

      {conflict && (
        <Text size="xs" tone="danger">
          That time overlaps an existing booking or block. Pick another time.
        </Text>
      )}

      <Row gap={2} align="center">
        <Button variant="ghost" size="sm" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
        <Spacer />
        <Button variant="primary" size="sm" loading={busy} onClick={onCommit}>
          Block time
        </Button>
      </Row>
    </Stack>
  );
}

export function TimeRangeEditor({
  editTimes,
  editBounds,
  zone,
  onAdjust,
  disabled,
}: {
  editTimes: EditTimes;
  editBounds: EditBounds;
  zone: string | null;
  onAdjust: (edge: 'start' | 'end', delta: -1 | 1) => void;
  disabled: boolean;
}) {
  return (
    <div className={styles.timeEditor}>
      <TimeStepper
        label="Start"
        value={formatListingClock(editTimes.start, zone)}
        canEarlier={editBounds.canEarlierStart}
        canLater={editBounds.canLaterStart}
        onEarlier={() => onAdjust('start', -1)}
        onLater={() => onAdjust('start', 1)}
        disabled={disabled}
      />
      <TimeStepper
        label="End"
        value={formatListingClock(editTimes.end, zone)}
        canEarlier={editBounds.canEarlierEnd}
        canLater={editBounds.canLaterEnd}
        onEarlier={() => onAdjust('end', -1)}
        onLater={() => onAdjust('end', 1)}
        disabled={disabled}
      />
    </div>
  );
}

function TimeStepper({
  label,
  value,
  canEarlier,
  canLater,
  onEarlier,
  onLater,
  disabled,
}: {
  label: string;
  value: string;
  canEarlier: boolean;
  canLater: boolean;
  onEarlier: () => void;
  onLater: () => void;
  disabled: boolean;
}) {
  return (
    <div className={styles.stepper}>
      <Text as="span" size="xs" tone="soft" weight="medium" className={styles.stepperLabel}>
        {label}
      </Text>
      <div className={styles.stepperControl}>
        <button
          type="button"
          className={styles.stepperButton}
          aria-label={`Move ${label.toLowerCase()} 30 minutes earlier`}
          disabled={disabled || !canEarlier}
          onClick={onEarlier}
        >
          <MinusGlyph />
        </button>
        <span className={cx(styles.stepperValue, 'tnum')}>{value}</span>
        <button
          type="button"
          className={styles.stepperButton}
          aria-label={`Move ${label.toLowerCase()} 30 minutes later`}
          disabled={disabled || !canLater}
          onClick={onLater}
        >
          <PlusGlyph />
        </button>
      </div>
    </div>
  );
}

function MinusGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3.5 8h9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function PlusGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 3.5v9M3.5 8h9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}
